import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  Loader2, IndianRupee, Calendar, Users, Sparkles, MapPin, Instagram, ClipboardList,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtInr = (n) => (n == null || n === '' ? '₹0' : `₹${Number(n).toLocaleString('en-IN')}`);
const fmtNum = (n) => {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString();
};

// Public read-only viewer hit by anyone with a share link.
// No sidebar, no actions — purely a presentation surface.
export const BrandPlanShareViewer = () => {
  const { token } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/campaign-plans/share/${token}`);
        setPlan(data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Link is invalid or expired');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return <div className="min-h-screen bg-white dark:bg-[#0A0B0D] flex items-center justify-center"><Loader2 className="animate-spin text-[#FF451A]" size={32} /></div>;
  }
  if (error || !plan) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0B0D] flex items-center justify-center p-8">
        <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center max-w-md" data-testid="share-not-found">
          <ClipboardList className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={44} />
          <h1 className="text-xl font-bold text-[#0F0F0F] dark:text-white mb-1">Plan unavailable</h1>
          <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">{error || 'This share link is invalid.'}</p>
        </div>
      </div>
    );
  }

  const s = plan.summary || {};

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0B0D] py-12 px-6" data-testid="brand-plan-share-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-2 font-mono">
          Campaign plan · shared by {plan.brand_name || 'a brand'}
        </div>
        <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-1" data-testid="share-plan-title">{plan.name}</h1>
        {plan.objective && <p className="text-[#6B6B6B] dark:text-zinc-400 mb-6">{plan.objective}</p>}

        {/* Top tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="share-summary-tiles">
          <Tile icon={IndianRupee} c="#00C972" label="Budget" value={fmtInr(plan.budget || 0)} />
          <Tile icon={IndianRupee} c="#FFB800" label="Est. spend" value={fmtInr(s.estimated_deliverable_spend || 0)} />
          <Tile icon={Users}       c="#FF451A" label="Creators" value={s.total_creators ?? 0} />
          <Tile icon={Sparkles}    c="#0055FF" label="Deliverables" value={s.total_deliverables ?? 0} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card title="Overview">
            <Row label="Timeline" icon={Calendar} value={`${plan.timeline_start || '—'} → ${plan.timeline_end || '—'}`} />
            <Row label="Categories" value={(plan.categories || []).join(', ') || '—'} />
            <Row label="Target locations" value={(plan.target_locations || []).join(', ') || '—'} />
          </Card>
          <Card title="Deliverables">
            <Row label="Reels" value={plan.num_reels || 0} />
            <Row label="Stories" value={plan.num_stories || 0} />
            <Row label="UGC assets" value={plan.num_ugc || 0} />
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <Coverage label="Categories represented" items={s.categories_represented || []} c="#FF451A" />
          <Coverage label="Cities represented"     items={s.cities_represented || []}     c="#0055FF" icon={MapPin} />
          <Coverage label="Languages represented"  items={s.languages_represented || []}  c="#00C972" />
        </div>

        <Card title={`Selected creators (${plan.creators?.length || 0})`}>
          <div className="space-y-2" data-testid="share-creators-list">
            {(plan.creators || []).map((c) => (
              <div key={c.id} className="bg-white/5 border border-black/[0.10] dark:border-white/10 rounded-xl p-3 flex items-center gap-3" data-testid="share-creator-row">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-sm font-bold shrink-0">
                  {c.name?.[0]?.toUpperCase() || 'C'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white truncate">{c.name}</div>
                  {c.instagram_handle && (
                    <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-400 inline-flex items-center gap-1">
                      <Instagram size={10} /> @{c.instagram_handle} · {fmtNum(c.instagram_followers)} followers
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-400 font-mono">
                  Reel {fmtInr(c.pricing_reel)} · Story {fmtInr(c.pricing_story)}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="text-center text-[10px] text-[#A1A1AA] dark:text-zinc-600 mt-8 font-mono uppercase tracking-wider">
          Read-only · Powered by Vyvre
        </div>
      </div>
    </div>
  );
};

const Tile = ({ icon: Icon, c, label, value }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-4">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1.5 font-mono">
      <Icon size={11} style={{ color: c }} /> {label}
    </div>
    <div className="text-2xl font-bold text-[#0F0F0F] dark:text-white">{value}</div>
  </div>
);

const Card = ({ title, children }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5">
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-4 font-mono">{title}</div>
    <div className="space-y-2">{children}</div>
  </div>
);

const Row = ({ label, value, icon: Icon }) => (
  <div className="flex items-start justify-between gap-3 text-sm">
    <div className="text-[#6B6B6B] dark:text-zinc-400 inline-flex items-center gap-1.5">
      {Icon && <Icon size={11} className="text-[#6B6B6B] dark:text-zinc-500" />} {label}
    </div>
    <div className="text-[#0F0F0F] dark:text-white font-medium text-right">{value}</div>
  </div>
);

const Coverage = ({ label, items, c, icon: Icon }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-4">
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-2 font-mono">{label}</div>
    {items.length === 0 ? <div className="text-xs text-[#6B6B6B] dark:text-zinc-500">—</div> : (
      <div className="flex flex-wrap gap-1">
        {items.slice(0, 12).map((it) => (
          <span key={it} className="px-2 py-0.5 text-[11px] rounded-full font-medium inline-flex items-center gap-1"
            style={{ backgroundColor: `${c}15`, color: c, border: `1px solid ${c}30` }}>
            {Icon && <Icon size={9} />} {it}
          </span>
        ))}
      </div>
    )}
  </div>
);
